/**
 * Device-local memory of which real account each redaction token stands for.
 *
 * The server only ever sees tokens like "[CARD-A]". This map, which ties a
 * token to the card's last four digits (and an optional nickname), lives in
 * this browser's localStorage and is never sent anywhere. It also keeps the
 * tokens stable: the same card gets the same letter on every statement
 * scrubbed on this device, which is what lets charges group across months.
 */

export interface TokenRecord {
  kind: string;
  /** Last four digits, enough to recognise the account, never the whole number. */
  last4: string;
  nickname?: string;
}

interface VaultData {
  /** identity (e.g. "card:6070") -> token (e.g. "[CARD-A]"), what the scrubber needs. */
  identities: Record<string, string>;
  /** token -> what to show for it. */
  tokens: Record<string, TokenRecord>;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

const KEY = "paredown.vault.v1";
const EMPTY: VaultData = { identities: {}, tokens: {} };

function storage(): StorageLike | undefined {
  try {
    return typeof localStorage === "undefined" ? undefined : localStorage;
  } catch {
    return undefined;
  }
}

export function loadVault(store: StorageLike | undefined = storage()): VaultData {
  try {
    const raw = store?.getItem(KEY);
    if (!raw) return structuredClone(EMPTY);
    const parsed = JSON.parse(raw) as Partial<VaultData>;
    return { identities: parsed.identities ?? {}, tokens: parsed.tokens ?? {} };
  } catch {
    return structuredClone(EMPTY);
  }
}

function saveVault(data: VaultData, store: StorageLike | undefined = storage()): void {
  try {
    store?.setItem(KEY, JSON.stringify(data));
  } catch {
    // Private windows or blocked storage: tokens still work for this upload.
  }
}

/** The identity -> token map the scrubber mutates while planning a redaction. */
export function tokenMapFromVault(store?: StorageLike): Map<string, string> {
  return new Map(Object.entries(loadVault(store).identities));
}

/** Persist the scrubber's map back, recording a label for every new token. */
export function rememberTokens(map: Map<string, string>, store?: StorageLike): void {
  const data = loadVault(store);
  for (const [identity, token] of map) {
    data.identities[identity] = token;
    if (!data.tokens[token]) {
      const [kind = "number", digits = ""] = identity.split(":");
      data.tokens[token] = { kind, last4: digits.slice(-4) };
    }
  }
  saveVault(data, store);
}

export function setNickname(token: string, nickname: string, store?: StorageLike): void {
  const data = loadVault(store);
  const rec = data.tokens[token];
  if (!rec) return;
  if (nickname.trim()) rec.nickname = nickname.trim();
  else delete rec.nickname;
  saveVault(data, store);
}

const KIND_WORD: Record<string, string> = { card: "Card", account: "Account", routing: "Routing", number: "Number" };

/** Human label for one token: "Sapphire ••6070", "Card ••6070", or the token itself if unknown here. */
export function labelFor(token: string, store?: StorageLike): string {
  const rec = loadVault(store).tokens[token];
  if (!rec) return token;
  const tail = rec.last4 ? ` ••${rec.last4}` : "";
  return `${rec.nickname ?? KIND_WORD[rec.kind] ?? rec.kind}${tail}`;
}

/** Replace every token in a string with its label. */
export function labelTokens(text: string | undefined, store?: StorageLike): string {
  if (!text) return "";
  return text.replace(/\[(?:CARD|ACCT|ROUTING|NUM)-[A-Z]+\]/g, (t) => labelFor(t, store));
}
